import { DriverApplication, DriverApplicationRequest, DriverApplicationStats } from '../types';
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:5000';
const API_BASE_URL = `${BACKEND_URL}/api`;

class DriverApplicationAPI {
  private getAuthHeader(): Record<string, string> {
    const token = localStorage.getItem('token');
    return token ? { Authorization: `Bearer ${token}` } : {};
  }

  // Submit a new driver application
  async submitApplication(
    applicationData: DriverApplicationRequest,
    documents: File[]
  ): Promise<{ success: boolean; application: DriverApplication; message: string }> {
    const formData = new FormData();

    // Add application data
    Object.entries(applicationData).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        formData.append(key, value.toString());
      }
    });

    // Add documents
    documents.forEach((file, index) => {
      formData.append('documents', file);
    });

    const response = await fetch(`${API_BASE_URL}/driver-applications`, {
      method: 'POST',
      headers: {
        ...this.getAuthHeader(),
        // Don't set Content-Type for FormData, let browser set it with boundary
      },
      body: formData,
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to submit application');
    }

    return response.json();
  }

  // Get user's own driver application
  async getMyApplication(): Promise<{ success: boolean; application: DriverApplication }> {
    const response = await fetch(`${API_BASE_URL}/driver-applications/my-application`, {
      headers: {
        'Content-Type': 'application/json',
        ...this.getAuthHeader(),
      },
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to fetch application');
    }

    return response.json();
  }

  // Update user's own driver application
  async updateMyApplication(
    applicationData: Partial<DriverApplicationRequest>,
    newDocuments?: File[]
  ): Promise<{ success: boolean; application: DriverApplication; message: string }> {
    const formData = new FormData();

    // Add application data
    Object.entries(applicationData).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        formData.append(key, value.toString());
      }
    });

    // Add new documents if provided
    if (newDocuments && newDocuments.length > 0) {
      newDocuments.forEach((file) => {
        formData.append('documents', file);
      });
    }

    const response = await fetch(`${API_BASE_URL}/driver-applications/my-application`, {
      method: 'PUT',
      headers: {
        ...this.getAuthHeader(),
      },
      body: formData,
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to update application');
    }

    return response.json();
  }

  // Get all driver applications (admin only)
  async getAllApplications(params?: {
    page?: number;
    limit?: number;
    status?: 'pending' | 'approved' | 'rejected';
  }): Promise<{
    success: boolean;
    applications: DriverApplication[];
    pagination: {
      page: number;
      limit: number;
      total: number;
      pages: number;
    };
  }> {
    const searchParams = new URLSearchParams();
    
    if (params?.page) searchParams.append('page', params.page.toString());
    if (params?.limit) searchParams.append('limit', params.limit.toString());
    if (params?.status) searchParams.append('status', params.status);

    const response = await fetch(
      `${API_BASE_URL}/driver-applications?${searchParams.toString()}`,
      {
        headers: {
          'Content-Type': 'application/json',
          ...this.getAuthHeader(),
        },
      }
    );

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to fetch applications');
    }

    return response.json();
  }

  // Get specific driver application (admin only)
  async getApplication(id: number): Promise<{ success: boolean; application: DriverApplication }> {
    const response = await fetch(`${API_BASE_URL}/driver-applications/${id}`, {
      headers: {
        'Content-Type': 'application/json',
        ...this.getAuthHeader(),
      },
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to fetch application');
    }

    return response.json();
  }

  // Review driver application (admin only)
  async reviewApplication(
    id: number,
    reviewData: { status: 'approved' | 'rejected'; admin_comments?: string }
  ): Promise<{ success: boolean; application: DriverApplication; message: string }> {
    const response = await fetch(`${API_BASE_URL}/driver-applications/${id}/review`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...this.getAuthHeader(),
      },
      body: JSON.stringify(reviewData),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to review application');
    }

    return response.json();
  }

  // Download document
  async downloadDocument(applicationId: number, documentId: string): Promise<Blob> {
    const response = await fetch(
      `${API_BASE_URL}/driver-applications/${applicationId}/documents/${documentId}`,
      {
        headers: {
          ...this.getAuthHeader(),
        },
      }
    );

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to download document');
    }

    return response.blob();
  }

  // Delete document from application
  async deleteDocument(documentId: string): Promise<{ success: boolean; message: string }> {
    const response = await fetch(
      `${API_BASE_URL}/driver-applications/my-application/documents/${documentId}`,
      {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          ...this.getAuthHeader(),
        },
      }
    );

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to delete document');
    }

    return response.json();
  }

  // Get application statistics (admin only)
  async getApplicationStats(): Promise<{ success: boolean; stats: DriverApplicationStats }> {
    const response = await fetch(`${API_BASE_URL}/driver-applications/stats/summary`, {
      headers: {
        'Content-Type': 'application/json',
        ...this.getAuthHeader(),
      },
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to fetch statistics');
    }

    return response.json();
  }
}

export const driverApplicationAPI = new DriverApplicationAPI();
